import {
  ParagraphNode,
  SerializedParagraphNode,
  LexicalNode,
  EditorConfig,
  NodeKey,
  $applyNodeReplacement,
  LexicalUpdateJSON,
} from 'lexical';
import * as Constants from 'const';
import { toPx } from 'utils/toPx';
import { setNodeLineHeightToDOM } from '../Plugins/TextBlockPlugin/utils';

const NODE_TYPE = 'custom-paragraph';

export type SerializedCustomParagraphNode = SerializedParagraphNode & {
  type: typeof NODE_TYPE;
  lineHeight?: number;
  fontSize?: number;
};

const DEFAULT_FONT_SIZE = Constants.DefaultCustomStyle.FONT_SIZE as number;

const calcFontSize = (fontSize = DEFAULT_FONT_SIZE): string => {
  return toPx(fontSize);
};

/**
* Custom Paragraph Node with dynamic line-height functionality
*/
export class CustomParagraphNode extends ParagraphNode {
  __lineHeight: number | undefined;

  __fontSize: number | undefined;

  constructor(lineHeight?: number, fontSize?: number, key?: NodeKey) {
    super(key);
    this.__lineHeight = lineHeight;
    this.__fontSize = fontSize;
  }

  static getType(): typeof NODE_TYPE {
    return NODE_TYPE;
  }

  static clone(node: CustomParagraphNode): CustomParagraphNode {
    return new CustomParagraphNode(node.__lineHeight, node.__fontSize, node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.style.fontSize = calcFontSize(this.__fontSize);
    setNodeLineHeightToDOM(element, this.__lineHeight, this.__fontSize);

    return element;
  }

  updateDOM(prevNode: CustomParagraphNode, element: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode, element, config);

    const prevLineHeight = prevNode.__lineHeight;
    const nextLineHeight = this.__lineHeight;
    const prevSize = prevNode.__fontSize;
    const nextSize = this.__fontSize;

    if ((prevLineHeight !== nextLineHeight) || (prevSize !== nextSize)) {
      element.style.fontSize = calcFontSize(nextSize);
      setNodeLineHeightToDOM(element, this.__lineHeight, this.__fontSize);
    }

    return isUpdated;
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedCustomParagraphNode>,
  ): this {
    super.updateFromJSON(serializedNode);

    return this
      .setLineHeight(serializedNode.lineHeight)
      .updateFontSize(serializedNode.fontSize);
  }

  static importJSON(
    serializedNode: SerializedCustomParagraphNode,
  ): CustomParagraphNode {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    return $createCustomParagraphNode().updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedCustomParagraphNode {
    const json = super.exportJSON();

    return {
      ...json,
      lineHeight: this.getLineHeight(),
      fontSize: this.getFontSize(),
    };
  }

  setLineHeight(lineHeight: number | undefined): this {
    this.getWritable().__lineHeight = lineHeight;

    return this;
  }

  getLineHeight(): number | undefined {
    return this.getLatest().__lineHeight;
  }

  setFontSize(fontSize: number | undefined): this {
    this.getWritable().__fontSize = fontSize;

    return this;
  }

  getFontSize(): number | undefined {
    return this.getLatest().__fontSize;
  }

  updateFontSize(fontSize: number | undefined): this {
    if (this.getFontSize() === fontSize) {
      return this;
    }

    return this.setFontSize(fontSize);
  }

}

export function $createCustomParagraphNode(lineHeight?: number, fontSize?: number): CustomParagraphNode {
  return $applyNodeReplacement(new CustomParagraphNode(lineHeight, fontSize));
}

export function $isCustomParagraphNode(
  node: LexicalNode | null | undefined,
): node is CustomParagraphNode {
  return node instanceof CustomParagraphNode;
}
